/**
 * CustomerTopUpPage — top up the citizen's NIPOST Pay wallet.
 *
 * Wired endpoints (service-financial-services):
 *   - POST /v1/wallet/wallets/customers/:customerId/get-or-create
 *   - POST /v1/wallet/credits — record a TOPUP_CARD / TOPUP_TRANSFER credit.
 *
 * RBAC: each endpoint requires `Wallet:create` / `WalletTransaction:credit`
 * and is `@OperatorScoped`. Citizens lack these permissions today (Phase 1c).
 *
 * Note: the actual money rails (Paystack / bank / USSD) ride a separate
 * service-payment integration that isn't in scope here. The credit endpoint
 * records the wallet transaction once payment has been confirmed; in the
 * absence of that integration the page demonstrates the client-side flow
 * and surfaces the deferral.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Hash,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  getOrCreateWallet,
  listOwnWalletTransactions,
  recordCredit,
  type ListWalletTransactionsResponse,
  type WalletResponse,
  type WalletTransactionResponse,
} from '../../lib/api/wallet';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader } from './PortalUI';

const QUICK_AMOUNTS: readonly number[] = [1000, 5000, 10000, 50000];
const HISTORY_PAGE_SIZE = 10;

type PaymentTab = 'card' | 'bank' | 'ussd';

export function customerWalletQueryKey(customerId: string): readonly [string, string] {
  return ['customer-wallet', customerId] as const;
}

export function customerWalletHistoryQueryKey(
  customerId: string,
  page: number,
): readonly [string, string, number] {
  return ['customer-wallet-history', customerId, page] as const;
}

/** Format an ISO timestamp into a short, locale-aware "date · time" string. */
function formatTxnDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

/** Human-friendly action label. Falls back to the raw action when unknown. */
function formatAction(action: string): string {
  switch (action) {
    case 'TOPUP_CASH':
      return 'Cash top-up';
    case 'TOPUP_CARD':
      return 'Card top-up';
    case 'TOPUP_TRANSFER':
      return 'Bank top-up';
    case 'P2P_SEND':
      return 'P2P sent';
    case 'P2P_RECEIVE':
      return 'P2P received';
    case 'BILL_PAYMENT':
      return 'Bill payment';
    case 'AIRTIME':
      return 'Airtime';
    case 'MAIL_PAYMENT':
      return 'Mail payment';
    case 'GOV_PAYMENT':
      return 'Government payment';
    case 'WITHDRAWAL_CASH':
      return 'Cash withdrawal';
    case 'WITHDRAWAL_TRANSFER':
      return 'Bank withdrawal';
    case 'DISBURSEMENT':
      return 'Disbursement';
    default:
      return action;
  }
}

/** Resolve the post-transaction balance from either shape (full / Phase 1c). */
function pickBalanceAfter(txn: WalletTransactionResponse): string | undefined {
  return txn.balanceAfter ?? txn.resultingBalance;
}

/** Format a decimal-string amount as ₦-prefixed money. */
function formatAmount(amount: string): string {
  // eslint-disable-next-line upmp/no-number-money -- Display-only formatter; the server-side string remains the source of truth.
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CustomerTopUpPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  // Wallet identity uses upmp_user_id, not the customer-profile id — see
  // CustomerClaims.upmpUserId in lib/auth/jwt.ts for why.
  const customerId = claims?.upmpUserId ?? '';

  const [amount, setAmount] = useState<string>('');
  const [tab, setTab] = useState<PaymentTab>('card');
  const [historyPage, setHistoryPage] = useState<number>(1);

  const wallet = useQuery<WalletResponse, Error>({
    queryKey: customerWalletQueryKey(customerId),
    queryFn: () => getOrCreateWallet(customerId, { bearerToken: token ?? undefined }),
    enabled: customerId.length > 0 && token !== null,
    retry: false,
    staleTime: 30_000,
  });

  const history = useQuery<ListWalletTransactionsResponse, Error>({
    queryKey: customerWalletHistoryQueryKey(customerId, historyPage),
    queryFn: () =>
      listOwnWalletTransactions({
        bearerToken: token ?? undefined,
        page: historyPage,
        pageSize: HISTORY_PAGE_SIZE,
      }),
    enabled: token !== null,
    retry: false,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });

  const topUp = useMutation<WalletTransactionResponse, Error, undefined>({
    mutationFn: () => {
      if (!wallet.data) {
        throw new Error('Wallet not loaded.');
      }
      const action = tab === 'card' ? 'TOPUP_CARD' : tab === 'bank' ? 'TOPUP_TRANSFER' : 'TOPUP_TRANSFER';
      return recordCredit(
        {
          walletId: wallet.data.id,
          action,
          amount: amount.length > 0 ? amount : '0',
          currency: wallet.data.currency,
          counterRef: `client-topup-${Date.now()}`,
          idempotencyKey: `topup-${Date.now()}`,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
      queryClient.invalidateQueries({ queryKey: ['customer-wallet-history', customerId] });
      setHistoryPage(1);
    },
  });

  const totalHistoryPages = Math.max(
    1,
    Math.ceil((history.data?.total ?? 0) / HISTORY_PAGE_SIZE),
  );

  function rbacError(err: Error): string {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return 'Your session has expired. Please sign in again.';
    }
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      return 'The wallet service is temporarily unavailable. Please try again in a moment.';
    }
    return 'We couldn’t complete the top-up. Please try again.';
  }

  function handlePay(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // eslint-disable-next-line upmp/no-number-money -- Client-side empty-input guard only; the wire payload still ships the amount as a decimal string.
    if (!amount || Number(amount) <= 0) return;
    topUp.mutate(undefined);
  }

  return (
    <div>
      <PageHeader title="Top Up Wallet" description="Add funds to your NIPOST Pay wallet." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <p className="text-sm text-muted mb-1">Current balance</p>
            {wallet.isLoading ? (
              <p className="text-sm text-muted flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : wallet.isError ? (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2 rounded" role="alert">
                {rbacError(wallet.error)}
              </p>
            ) : wallet.data ? (
              <>
                <p className="text-4xl font-bold text-nipost-green">
                  {/* eslint-disable-next-line upmp/no-number-money -- Display-only formatter; the server-side balance string remains the source of truth. */}
                  ₦{Number(wallet.data.balance).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted mt-2 font-mono">Wallet id: {wallet.data.id.slice(0, 12)}</p>
              </>
            ) : null}
          </div>

          <form onSubmit={handlePay} className="bg-white rounded-xl border border-border p-6 space-y-5" noValidate>
            <h3 className="font-semibold text-gray-900">Enter amount</h3>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-500">₦</span>
              <input
                type="number"
                min="0"
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                aria-label="Amount"
                className="w-full pl-10 pr-4 py-4 text-2xl font-semibold border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                    String(q) === amount
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'border-border text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  ₦{q.toLocaleString()}
                </button>
              ))}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Payment method</h3>
              <div className="flex border-b border-border mb-4">
                {([
                  { key: 'card' as const, label: 'Card', icon: CreditCard },
                  { key: 'bank' as const, label: 'Bank transfer', icon: Building2 },
                  { key: 'ussd' as const, label: 'USSD', icon: Hash },
                ] as const).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                      tab === t.key
                        ? 'border-nipost-green text-nipost-green'
                        : 'border-transparent text-muted hover:text-gray-700'
                    }`}
                  >
                    <t.icon className="w-4 h-4" /> {t.label}
                  </button>
                ))}
              </div>

              <p className="text-xs text-muted">
                Live payment rails (Paystack, bank, USSD) are being prepared. Submitting below
                records a wallet credit as if payment had cleared, for preview purposes.
              </p>
            </div>

            {topUp.isError ? (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
                {rbacError(topUp.error)}
              </div>
            ) : null}

            {topUp.isSuccess ? (
              <div className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2" role="status">
                <CheckCircle2 className="w-3.5 h-3.5" /> Wallet credited (transaction id {topUp.data.id.slice(0, 8)}).
              </div>
            ) : null}

            { }
            <button
              type="submit"
              // eslint-disable-next-line upmp/no-number-money -- guard: empty/non-positive form input — not arithmetic on stored money
              disabled={!amount || Number(amount) <= 0 || topUp.isPending || !wallet.data}
              className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {topUp.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {/* eslint-disable-next-line upmp/no-number-money -- Display-only formatter. */}
              Pay {amount ? `— ₦${Number(amount).toLocaleString()}` : ''}
            </button>
          </form>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Wallet history</h3>
              {history.isFetching && !history.isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted" aria-label="Refreshing" />
              ) : null}
            </div>

            {history.isLoading ? (
              <ul className="space-y-2" aria-label="Loading wallet history" aria-busy="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <li key={i} className="animate-pulse">
                    <div className="h-3 bg-gray-100 rounded w-1/3 mb-1.5" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                  </li>
                ))}
              </ul>
            ) : history.isError ? (
              <div className="space-y-2" role="alert">
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2 rounded">
                  Could not load history.
                </p>
                <button
                  type="button"
                  onClick={() => history.refetch()}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-nipost-green hover:underline"
                >
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              </div>
            ) : history.data && history.data.items.length === 0 ? (
              <p className="text-xs text-muted">
                No wallet activity yet. Your top-ups, payments, and transfers will appear here.
              </p>
            ) : history.data ? (
              <>
                <ul className="divide-y divide-border -mx-1" aria-label="Wallet transactions">
                  {history.data.items.map((txn) => {
                    const isCredit = txn.side === 'CREDIT';
                    const balance = pickBalanceAfter(txn);
                    return (
                      <li key={txn.id} className="px-1 py-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {formatAction(txn.action)}
                            </p>
                            <p className="text-[11px] text-muted">
                              {formatTxnDate(txn.initiatedAt ?? txn.createdAt)}
                            </p>
                            {txn.counterRef ? (
                              <p className="text-[11px] text-muted font-mono truncate">
                                {txn.counterRef}
                              </p>
                            ) : null}
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className={`text-sm font-semibold ${
                                isCredit ? 'text-nipost-green' : 'text-gray-900'
                              }`}
                            >
                              {isCredit ? '+' : '−'}₦{formatAmount(txn.amount)}
                            </p>
                            {balance ? (
                              <p className="text-[11px] text-muted">
                                Bal ₦{formatAmount(balance)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={historyPage <= 1 || history.isFetching}
                    aria-label="Previous page"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 border border-border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Prev
                  </button>
                  <span className="text-[11px] text-muted" aria-live="polite">
                    Page {historyPage} of {totalHistoryPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                    disabled={historyPage >= totalHistoryPages || history.isFetching}
                    aria-label="Next page"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 border border-border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
